/**
 * Mobile Header Component
 * Features: Hamburger Menu Icon, Logo, User Avatar
 * Displays on screens < lg (1024px)
 */

import React from 'react';
import { Box, IconButton, Avatar, Typography } from '@mui/material';
import { LogIn, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { User } from '../../types';
import { NotificationBell } from '../../features/notifications/components/NotificationBell';

// ============================================
// Props Interface
// ============================================

interface MobileHeaderProps {
  user: User;
  isAnonymous?: boolean;
  onMenuClick: () => void;
  onSignIn?: () => void;
}

// ============================================
// MobileHeader Component
// ============================================

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  user,
  isAnonymous = false,
  onMenuClick,
  onSignIn,
}) => {
  const { t } = useTranslation();
  const displayName = user.fullName || user.userName;
  const avatarInitial = (displayName || user.email || 'U').charAt(0).toUpperCase();

  return (
    <Box className="fixed left-0 right-0 top-0 z-50 grid h-16 grid-cols-3 items-center border-b border-gray-200 bg-white px-4 dark:border-slate-700 dark:bg-slate-800 lg:hidden">
      {/* Left Section: Hamburger Menu */}
      <Box className="flex justify-start">
        <IconButton
          onClick={onMenuClick}
          className="text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
          sx={{ minWidth: 44, minHeight: 44 }} // Touch target optimization
        >
          <Menu size={24} />
        </IconButton>
      </Box>

      {/* Center Section: Logo */}
      <Link to="/new-chat" className="flex items-center justify-center gap-2 no-underline">
        <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
        <Typography
          variant="h6"
          className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text font-bold text-transparent dark:from-blue-400 dark:to-cyan-400"
        >
          AppAI
        </Typography>
      </Link>

      {/* Right Section */}
      {isAnonymous ? (
        <Box className="flex justify-end">
          <button
            type="button"
            onClick={onSignIn}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-teal-600 px-3 text-sm font-medium text-white transition-colors hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
          >
            <LogIn size={16} />
            <span>{t('common.signIn')}</span>
          </button>
        </Box>
      ) : (
        <Box className="flex items-center justify-end gap-1">
          <NotificationBell />
          <Avatar
            src={user.avatar}
            alt={displayName}
            className="bg-gradient-to-br from-indigo-500 to-pink-500"
            sx={{ width: 36, height: 36 }}
          >
            {avatarInitial}
          </Avatar>
        </Box>
      )}
    </Box>
  );
};
